import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Play } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { Icon } from "@/components/Icon";
import { IMAGES, PILLARS } from "@/data/site";

export const Hero = ({ onJoin, onWatch }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden grain" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img src={IMAGES.heroField} alt="Aerial farmland" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(7,22,16,0.84)" }} />
        <div className="absolute inset-0 spotlight" />
      </motion.div>

      <svg className="absolute bottom-0 left-0 w-full h-[34vh] z-10" viewBox="0 0 1440 320" preserveAspectRatio="none">
        {[0, 26, 54, 86].map((o, i) => (
          <motion.path
            key={i}
            className="hill-line"
            d={`M0 ${200 + o} C 240 ${120 + o}, 420 ${250 + o}, 720 ${180 + o} S 1180 ${110 + o}, 1440 ${210 + o}`}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.42 - i * 0.08 }}
            transition={{ duration: 2.4, delay: 0.6 + i * 0.18, ease: "easeInOut" }}
          />
        ))}
      </svg>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[170px] pb-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="eyebrow">
          KMG Farm · The A grade farmers
        </motion.p>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mt-8 max-w-4xl">
          <MaskedLines
            delay={0.35}
            lines={[
              "Smart farm.",
              <>
                <span className="font-editorial italic text-[color:var(--kmg-lime)]">Smarter</span> future.
              </>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-mono-label text-[11px] sm:text-xs uppercase tracking-[0.28em] mt-9 text-[color:var(--kmg-gold)]"
        >
          The All-in-One Digital ERP for Agriculture
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-lg sm:text-xl mt-7 max-w-2xl text-[color:var(--kmg-bone-dim)] leading-relaxed"
        >
          KMG Digi Platform is a powerful digital ecosystem that helps farmers and agribusinesses
          manage every aspect of their operations, make data-driven decisions, connect with partners
          and grow sustainably — from farm to market.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.12, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-center gap-4 mt-12"
        >
          <button
            data-testid="hero-join-btn"
            onClick={onJoin}
            className="rounded-full bg-[color:var(--kmg-lime)] px-8 py-4 text-sm font-semibold text-[color:var(--kmg-forest)] hover:bg-[#8df0b3]"
            style={{ transition: "background-color .4s ease" }}
          >
            Get started today
          </button>
          <button
            data-testid="hero-watch-btn"
            onClick={onWatch}
            className="inline-flex items-center gap-3 rounded-full border border-[rgba(244,241,230,0.28)] px-7 py-4 text-sm hover:border-[color:var(--kmg-lime)] hover:text-[color:var(--kmg-lime)]"
            style={{ transition: "border-color .4s ease, color .4s ease" }}
          >
            <Play size={15} /> Watch the platform film
          </button>
        </motion.div>

        <p className="font-editorial italic text-xl sm:text-2xl mt-16 text-[color:var(--kmg-bone-dim)]">
          Manage. Monitor. Grow.
        </p>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-9 gap-x-6 border-t border-[rgba(244,241,230,0.14)] pt-10">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 + i * 0.08, duration: 0.7 }}
              data-testid={`hero-pillar-${i}`}
            >
              <Icon name={p.icon} size={20} className="text-[color:var(--kmg-lime)]" />
              <p className="font-display text-sm mt-4">{p.title}</p>
              <p className="text-xs mt-1.5 text-[color:var(--kmg-bone-dim)] opacity-80">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-6 z-20 hidden sm:flex items-center gap-3 font-mono-label text-[10px] uppercase tracking-[0.25em] text-[color:var(--kmg-bone-dim)]"
      >
        Scroll
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
